// Universal Service Offering Types - works across all service categories

import { ServiceCategory } from './serviceWizard';

export type OfferingPriceType = 'fixed' | 'quote' | 'contact' | 'starting_at';

// Category-specific offering types
export const OFFERING_TYPES_BY_CATEGORY: Record<ServiceCategory, { value: string; label: string }[]> = {
  mro: [
    { value: 'maintenance_package', label: 'Maintenance Package' },
    { value: 'inspection', label: 'Inspection Service' },
    { value: 'overhaul', label: 'Overhaul Service' },
    { value: 'repair', label: 'Repair Service' },
    { value: 'modification', label: 'Modification / Upgrade' },
    { value: 'aog_response', label: 'AOG Response' },
  ],
  camo: [
    { value: 'camo_package', label: 'CAMO Package' },
    { value: 'arc_service', label: 'ARC Issuance' },
    { value: 'compliance_review', label: 'AD/SB Compliance Review' },
    { value: 'records_management', label: 'Records Management' },
    { value: 'prepurchase_inspection', label: 'Pre-Purchase Inspection' },
    { value: 'lease_return', label: 'Lease Return Management' },
  ],
  part21: [
    { value: 'stc', label: 'STC Development' },
    { value: 'minor_mod', label: 'Minor Modification' },
    { value: 'major_mod', label: 'Major Modification' },
    { value: 'pma_part', label: 'PMA Part' },
    { value: 'kit', label: 'Installation Kit' },
    { value: 'publication', label: 'Technical Publication' },
  ],
  training: [
    { value: 'type_rating', label: 'Type Rating Course' },
    { value: 'recurrent', label: 'Recurrent Training' },
    { value: 'specialty_course', label: 'Specialty Course' },
    { value: 'simulator_session', label: 'Simulator Session' },
    { value: 'ground_school', label: 'Ground School' },
    { value: 'custom_program', label: 'Custom Training Program' },
  ],
  operator: [
    { value: 'charter_package', label: 'Charter Package' },
    { value: 'mission_rate', label: 'Mission Rate' },
    { value: 'wet_lease', label: 'Wet Lease' },
    { value: 'contract_service', label: 'Contract Service' },
    { value: 'hourly_rate', label: 'Hourly Rate' },
  ],
  engineering: [
    { value: 'consulting', label: 'Consulting Service' },
    { value: 'design_package', label: 'Design Package' },
    { value: 'analysis', label: 'Analysis / Report' },
    { value: 'certification_support', label: 'Certification Support' },
    { value: 'project_rate', label: 'Project Rate' },
  ],
  ground_ops: [
    { value: 'handling_package', label: 'Handling Package' },
    { value: 'fueling_rate', label: 'Fueling Rate' },
    { value: 'equipment_rental', label: 'Equipment Rental' },
    { value: 'security_service', label: 'Security Service' },
    { value: 'concierge', label: 'Concierge Service' },
  ],
  fbo_heliport: [
    { value: 'hangar_rental', label: 'Hangar Rental' },
    { value: 'parking', label: 'Parking / Tie-Down' },
    { value: 'fuel_rate', label: 'Fuel Rate' },
    { value: 'landing_fee', label: 'Landing Fee' },
    { value: 'facility_rental', label: 'Facility Rental' },
    { value: 'membership', label: 'Membership' },
  ],
  management: [
    { value: 'management_fee', label: 'Management Fee' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'acquisition_support', label: 'Acquisition Support' },
    { value: 'sales_support', label: 'Sales Support' },
    { value: 'fleet_analysis', label: 'Fleet Analysis' },
  ],
  uas: [
    { value: 'inspection_package', label: 'Inspection Package' },
    { value: 'survey_service', label: 'Survey Service' },
    { value: 'hourly_rate', label: 'Hourly Rate' },
    { value: 'project_rate', label: 'Project Rate' },
    { value: 'training', label: 'Training / Certification' },
  ],
};

// Lead time options
export const OFFERING_LEAD_TIMES = [
  { value: 'immediate', label: 'Immediate / Same Day' },
  { value: '1_3_days', label: '1-3 Days' },
  { value: '1_week', label: '1 Week' },
  { value: '2_weeks', label: '2 Weeks' },
  { value: '1_month', label: '1 Month' },
  { value: '2_3_months', label: '2-3 Months' },
  { value: 'custom', label: 'Custom / Contact' },
];

// Universal Service Offering interface
export interface ServiceOffering {
  id?: string;
  offering_type: string;
  name: string;
  description: string;
  price: number | null;
  price_type: OfferingPriceType;
  currency: string;
  lead_time: string;
  photos: string[];
  documentation_url: string;
  compatible_helicopters: string[];
  is_active: boolean;
  display_order: number;
  
  // Category-specific fields stored in a flexible object
  category_data: {
    // Part-21 specific
    part_number?: string;
    certification_basis?: string;
    
    // Training specific
    duration_value?: number;
    duration_unit?: 'hours' | 'days' | 'weeks' | 'months';
    prerequisites?: string;
    certification_issued?: string;
    max_students?: number;
    simulator_included?: boolean;
    
    // Operator specific
    minimum_booking?: string;
    included_services?: string[];
    
    // MRO specific
    estimated_downtime?: string;
    warranty_period?: string;
    
    // FBO specific
    capacity?: number;
    availability?: string;
  };
}

// Helper to get offering types for a category
export const getOfferingTypesForCategory = (category: ServiceCategory | null) => {
  if (!category) return [];
  return OFFERING_TYPES_BY_CATEGORY[category] || [];
};

// Initial offering state
export const createEmptyOffering = (): ServiceOffering => ({
  offering_type: '',
  name: '',
  description: '',
  price: null,
  price_type: 'quote',
  currency: 'USD',
  lead_time: '',
  photos: [],
  documentation_url: '',
  compatible_helicopters: [],
  is_active: true,
  display_order: 0,
  category_data: {},
});
